import * as React from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import { Button, ControlLabel, Label } from 'components/bootstrap';
import { IconButton, Icon } from 'components/common';
import { useModalContext } from 'common/contexts';
import { Row } from 'common/components';
import useCurrentUser from 'hooks/useCurrentUser';
import { TD } from 'security-app/components/common';
import SetStatusControl from 'security-app/components/SecurityEvents/Alerts/EventDetails/SetStatusControl';
import useSendEventActionTelemetry from 'components/events/events/hooks/useSendEventActionTelemetry';
import useOwnerAssignee from 'security-app/components/SecurityEvents/Alerts/hooks/useOwnerTitles';
import type { SecurityEventAPIType } from 'security-app/hooks/api/securityEventsAPI.types';

const RowCol = styled(Row)<{ $orientation: 'horz' | 'vert' }>`
  flex-direction: ${({ $orientation }) => ($orientation === 'vert' ? 'column' : 'row')};
`;

const FlexLabel = styled(Label)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

type Props = {
  securityEvent: SecurityEventAPIType;
  onReplaySearch: () => void;
  orientation?: 'horz' | 'vert';
};

function ActionsRow({ securityEvent, onReplaySearch, orientation = 'horz' }: Props) {
  const { setModal, setEntity } = useModalContext();
  const { permissions } = useCurrentUser();
  const { data: assignee } = useOwnerAssignee(securityEvent.owner);
  const sendEventActionTelemetry = useSendEventActionTelemetry();

  const currentOwnerName = React.useMemo(() => {
    if (assignee?.label) return assignee.label;
    if (securityEvent.owner) return 'Assigned';

    return undefined;
  }, [assignee, securityEvent.owner]);

  const hideReplayAction = React.useMemo(
    () =>
      !!securityEvent.event.event_definition_type.match(/system-notification|anomaly/i) ||
      !securityEvent.event.replay_info,
    [securityEvent],
  );
  const canManageSecurityEvents = React.useMemo(() => isPermitted(permissions, 'security_event:edit'), [permissions]);

  const replaySearch = () => {
    sendEventActionTelemetry('REPLAY_SEARCH', false);

    if (typeof onReplaySearch === 'function') {
      onReplaySearch();

      return;
    }

    setEntity(securityEvent);
    setModal('REPLAY-SEARCH-VIEW');
  };

  const openEvidenceModal = () => {
    const { event } = securityEvent;
    setEntity([event.id]);
    setModal('ADD-EVIDENCE');
  };

  const sendNotification = () => {
    setEntity(securityEvent);
    setModal('SEND-NOTIFICATION');
  };

  const onStatusChange = (newStatus: string) => {
    if (newStatus !== securityEvent.status) {
      const newSecurityEvent = { ...securityEvent, status: newStatus };
      setEntity(newSecurityEvent);
      setModal('CONFIRM-STATUS-CHANGE');
    }
  };

  const selectOwner = () => {
    setEntity(securityEvent);
    setModal('SELECT-OWNER');
  };

  const onRemoveOwner = () => {
    setEntity(securityEvent);
    setModal('CONFIRM-OWNER-REMOVE');
  };

  return (
    <RowCol $orientation={orientation}>
      <Row $align="center" $justify="flex-start" $gap="md">
        {hideReplayAction || (
          <Button bsStyle="primary" bsSize="sm" onClick={replaySearch}>
            Replay Search
          </Button>
        )}
        {!canManageSecurityEvents || (
          <Button bsSize="sm" onClick={openEvidenceModal}>
            Add to an Investigation
          </Button>
        )}
        {!canManageSecurityEvents || (
          <Button bsSize="sm" onClick={sendNotification}>
            Send Notification
          </Button>
        )}
      </Row>
      <Row $align="center" $justify="flex-end" $gap="xl">
        {canManageSecurityEvents ? (
          <Row $gap="xs" $align="center" $width="auto">
            <ControlLabel style={{ margin: 0 }}>Owner</ControlLabel>
            {!!currentOwnerName && (
              <FlexLabel>
                {currentOwnerName}
                <StyledIcon name="close" onClick={onRemoveOwner} />
              </FlexLabel>
            )}
            {!!currentOwnerName || <IconButton name="add" onClick={selectOwner} title="Add owner" />}
          </Row>
        ) : (
          <TD />
        )}
        <Row $align="center" $width="auto">
          <SetStatusControl
            onStatusChange={onStatusChange}
            value={securityEvent.status}
            disabled={!canManageSecurityEvents}
          />
        </Row>
      </Row>
    </RowCol>
  );
}

export default ActionsRow;
